import React, { useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Mail, ArrowRight, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';

// Post-register screen. The user is NOT authenticated. They must click
// the link in their inbox before they can log in.
export default function VerifyEmailRequiredPage() {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { resendVerification } = useAuth();
    const email = location.state?.email || '';
    const initialSent = location.state?.emailSent !== false;

    const [resendState, setResendState] = useState({ loading: false, ok: false, error: '' });
    const [manualEmail, setManualEmail] = useState(email);

    // If someone lands here without an email context (e.g. deep-link),
    // send them back to the safe entry point.
    if (!email && !location.state) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] px-4">
                <div className="max-w-md w-full bg-white p-8 rounded-lg border border-[#E5E7EB] text-center">
                    <p className="text-[#4B5563] mb-4">{t('auth.verify_required.no_flow_detected')}</p>
                    <Button onClick={() => navigate('/register')} data-testid="ver-req-back-register">
                        {t('auth.verify_required.go_register')}
                    </Button>
                </div>
            </div>
        );
    }

    const handleResend = async () => {
        const target = (manualEmail || email || '').trim();
        if (!target) return;
        setResendState({ loading: true, ok: false, error: '' });
        const res = await resendVerification(target);
        let errMsg = '';
        if (!res.success) {
            errMsg = res.errorCode
                ? t(`auth.errors.${res.errorCode}`, { defaultValue: res.error || t('auth.errors.generic') })
                : (res.error || t('auth.errors.generic'));
        }
        setResendState({
            loading: false,
            ok: res.success,
            error: errMsg,
        });
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] px-4 py-12" data-testid="verify-required-page">
            <div className="max-w-md w-full bg-white p-8 rounded-2xl border border-[#E5E7EB] shadow-sm">
                <div className="flex justify-center mb-6">
                    <div className="w-16 h-16 rounded-full bg-[#EEF2FF] flex items-center justify-center">
                        <Mail className="w-8 h-8 text-[#002FA7]" />
                    </div>
                </div>
                <h1 className="text-2xl font-bold text-center text-[#0A0A0A] mb-2 font-['Outfit']">
                    {t('auth.verify_required.title')}
                </h1>
                <p className="text-center text-[#4B5563] mb-6">
                    {t('auth.verify_required.intro')}
                </p>
                <p className="text-center text-[#0A0A0A] font-medium mb-6 break-all" data-testid="verify-required-email">
                    {email}
                </p>
                {!initialSent && (
                    <div className="mb-4 p-3 rounded-lg bg-[#FEF3C7] border border-[#FCD34D] text-sm text-[#92400E] flex gap-2">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        {t('auth.verify_required.email_send_failed')}
                    </div>
                )}
                <p className="text-sm text-[#6B7280] text-center mb-6">
                    {t('auth.verify_required.help')}
                </p>

                <div className="space-y-3">
                    <Button
                        onClick={handleResend}
                        disabled={resendState.loading || !manualEmail}
                        className="w-full bg-[#002FA7] hover:bg-[#001F70]"
                        data-testid="verify-required-resend-btn"
                    >
                        {resendState.loading ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('auth.verify_required.resend_loading')}</>
                        ) : (
                            <>{t('auth.verify_required.resend_btn')}</>
                        )}
                    </Button>
                    <Link to="/login" className="block" data-testid="verify-required-login-link">
                        <Button variant="outline" className="w-full">
                            {t('auth.verify_required.back_to_login')}
                            <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </Link>
                </div>

                {resendState.ok && (
                    <div className="mt-4 p-3 rounded-lg bg-[#D1FAE5] border border-[#059669] text-sm text-[#065F46] flex gap-2" data-testid="verify-required-resend-ok">
                        <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        {t('auth.verify_required.resend_success')}
                    </div>
                )}
                {resendState.error && (
                    <div className="mt-4 p-3 rounded-lg bg-[#FEE2E2] border border-[#EF4444] text-sm text-[#B91C1C]" data-testid="verify-required-resend-err">
                        {resendState.error}
                    </div>
                )}
            </div>
        </div>
    );
}
